import React, { useState, useEffect } from 'react';
import {
  Plus,
  Edit,
  Trash2,
  Users,
  Search,
  Filter,
  Download,
  User,
  Mail,
  Phone,
  Calendar,
  UserPlus,
  UserCheck,
  Save,
  X
} from 'lucide-react';
import { authAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import {
  ModernCard,
  ModernButton,
  ModernInput,
  ModernSelect,
  ModernTable,
  ModernBadge,
  ModernModal,
  ModernSearchBar,
  ModernLoadingSpinner
} from '../components/ui/ModernComponents';
import toast from 'react-hot-toast';
import EntitySelectWithAdd from '../components/ui/EntitySelectWithAdd';

const defaultForm = {
  name: '',
  email: '',
  phone: '',
  role: 'employee',
  store_id: '',
  hire_date: ''
};

const Employees = () => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(defaultForm);
  const [editingId, setEditingId] = useState(null);
  const [submitLoading, setSubmitLoading] = useState(false);
  const { hasAnyRole } = useAuth();

  useEffect(() => {
    fetchEmployees();
  }, []);

  const fetchEmployees = async () => {
    try {
      setLoading(true);
      const response = await authAPI.getEmployees();
      setEmployees(response.data);
    } catch (error) {
      console.error('Error fetching employees:', error);
      toast.error('Failed to load employees');
    } finally {
      setLoading(false);
    }
  };

  const filteredEmployees = employees.filter(employee =>
    String(employee.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    String(employee.email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    String(employee.role || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getRoleBadge = (role) => {
    const badges = {
      admin: 'danger',
      manager: 'warning',
      employee: 'primary'
    };
    return badges[role] || 'neutral';
  };

  // Handle form input
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  // Handle add/edit submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitLoading(true);
    try {
      if (editingId) {
        await authAPI.updateEmployee(editingId, form);
        toast.success('Employé modifié');
      } else {
        await authAPI.createEmployee(form);
        toast.success('Employé ajouté');
      }
      setShowForm(false);
      setForm(defaultForm);
      setEditingId(null);
      fetchEmployees();
    } catch (err) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSubmitLoading(false);
    }
  };

  // Handle edit
  const handleEdit = (employee) => {
    setForm({
      name: employee.name,
      email: employee.email,
      phone: employee.phone || '',
      role: employee.role,
      store_id: employee.store_id || '',
      hire_date: employee.hire_date ? employee.hire_date.split('T')[0] : ''
    });
    setEditingId(employee.id);
    setShowForm(true);
  };

  // Handle delete
  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cet employé ?')) return;
    try {
      await authAPI.deleteEmployee(id);
      toast.success('Employé supprimé');
      fetchEmployees();
    } catch (err) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Handle add new
  const handleAdd = () => {
    setForm(defaultForm);
    setEditingId(null);
    setShowForm(true);
  };

  const roleOptions = [
    { value: 'employee', label: 'Employé' },
    { value: 'manager', label: 'Manager' },
    { value: 'admin', label: 'Administrateur' }
  ];

  const tableColumns = [
    {
      key: 'name',
      label: 'Employé',
      render: (value, employee) => (
        <div className="flex items-center">
          <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center mr-3">
            <span className="text-sm font-medium text-primary-700">
              {employee.name.charAt(0).toUpperCase()}
            </span>
          </div>
          <div>
            <div className="font-semibold text-neutral-900">{employee.name}</div>
            <div className="text-sm text-neutral-500">{employee.email}</div>
          </div>
        </div>
      )
    },
    {
      key: 'role',
      label: 'Rôle',
      render: (value) => (
        <ModernBadge variant={getRoleBadge(value)} className="capitalize">
          {value}
        </ModernBadge>
      )
    },
    {
      key: 'phone',
      label: 'Téléphone',
      render: (value) => (
        <div className="flex items-center">
          <Phone className="w-4 h-4 text-neutral-400 mr-2" />
          <span className="font-medium">{value || '-'}</span>
        </div>
      )
    },
    {
      key: 'store_name',
      label: 'Magasin',
      render: (value) => (
        <div className="flex items-center">
          <div className="w-8 h-8 bg-success-100 rounded-lg flex items-center justify-center mr-2">
            <span className="text-xs font-medium text-success-700">S</span>
          </div>
          <span className="text-sm">{value || '-'}</span>
        </div>
      )
    },
    {
      key: 'hire_date',
      label: 'Date d\'embauche',
      render: (value) => (
        <div className="flex items-center">
          <Calendar className="w-4 h-4 text-neutral-400 mr-2" />
          <span className="text-sm">
            {value ? new Date(value).toLocaleDateString('fr-FR') : '-'}
          </span>
        </div>
      )
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (value, employee) => (
        <div className="flex items-center space-x-2">
          <ModernButton
            variant="ghost"
            size="sm"
            icon={<Edit className="w-4 h-4" />}
            onClick={() => handleEdit(employee)}
          >
            Voir
          </ModernButton>
          <ModernButton
            variant="primary"
            size="sm"
            icon={<Edit className="w-4 h-4" />}
            onClick={() => handleEdit(employee)}
          >
            Modifier
          </ModernButton>
          <ModernButton
            variant="danger"
            size="sm"
            icon={<Trash2 className="w-4 h-4" />}
            onClick={() => handleDelete(employee.id)}
          >
            Supprimer
          </ModernButton>
        </div>
      )
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <ModernLoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900">Employés</h1>
          <p className="text-neutral-600">Gérez votre équipe d'employés</p>
        </div>
        <div className="flex items-center space-x-3">
          <ModernSearchBar
            placeholder="Rechercher des employés..."
            onSearch={setSearchTerm}
            className="w-64"
            debounce={300}
          />
          {hasAnyRole(['admin']) && (
            <ModernButton
              variant="primary"
              icon={<Plus className="w-4 h-4" />}
              onClick={handleAdd}
            >
              Ajouter un employé
            </ModernButton>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <ModernCard className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-600">Total Employés</p>
              <p className="text-2xl font-bold text-neutral-900">{employees.length}</p>
            </div>
            <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
              <Users className="w-6 h-6 text-primary-600" />
            </div>
          </div>
        </ModernCard>

        <ModernCard className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-600">Managers</p>
              <p className="text-2xl font-bold text-warning-600">
                {employees.filter(e => e.role === 'manager').length}
              </p>
            </div>
            <div className="w-12 h-12 bg-warning-100 rounded-lg flex items-center justify-center">
              <UserCheck className="w-6 h-6 text-warning-600" />
            </div>
          </div>
        </ModernCard>

        <ModernCard className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-600">Administrateurs</p>
              <p className="text-2xl font-bold text-danger-600">
                {employees.filter(e => e.role === 'admin').length}
              </p>
            </div>
            <div className="w-12 h-12 bg-danger-100 rounded-lg flex items-center justify-center">
              <User className="w-6 h-6 text-danger-600" />
            </div>
          </div>
        </ModernCard>

        <ModernCard className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-600">Nouveaux</p>
              <p className="text-2xl font-bold text-success-600">
                {employees.filter(e => {
                  const hireDate = new Date(e.hire_date || Date.now());
                  const weekAgo = new Date();
                  weekAgo.setDate(weekAgo.getDate() - 7);
                  return hireDate > weekAgo;
                }).length}
              </p>
            </div>
            <div className="w-12 h-12 bg-success-100 rounded-lg flex items-center justify-center">
              <UserPlus className="w-6 h-6 text-success-600" />
            </div>
          </div>
        </ModernCard>
      </div>

      {/* Employees Table */}
      <ModernCard className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-neutral-900">Liste des employés</h3>
            <p className="text-sm text-neutral-600">{filteredEmployees.length} employés trouvés</p>
          </div>
          <div className="flex items-center space-x-2">
            <ModernButton variant="ghost" size="sm" icon={<Filter className="w-4 h-4" />}>
              Filtrer
            </ModernButton>
            <ModernButton variant="ghost" size="sm" icon={<Download className="w-4 h-4" />}>
              Exporter
            </ModernButton>
          </div>
        </div>

        <ModernTable
          columns={tableColumns}
          data={filteredEmployees}
          className="w-full"
        />
      </ModernCard>

      {/* Add/Edit Modal */}
      <ModernModal
        isOpen={showForm}
        onClose={() => setShowForm(false)}
        title={editingId ? 'Modifier l\'employé' : 'Ajouter un employé'}
        size="lg"
      >
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <ModernInput
              label="Nom complet"
              name="name"
              value={form.name}
              onChange={handleChange}
              placeholder="Ex: Jean Dupont"
              icon={<User className="w-5 h-5" />}
              required
            />

            <ModernInput
              label="Email"
              name="email"
              type="email"
              value={form.email}
              onChange={handleChange}
              placeholder="Ex: jean.dupont@example.com"
              icon={<Mail className="w-5 h-5" />}
              required
            />

            <ModernInput
              label="Téléphone"
              name="phone"
              value={form.phone}
              onChange={handleChange}
              placeholder="Ex: +33 1 23 45 67 89"
              icon={<Phone className="w-5 h-5" />}
            />

            {/* Replace ModernSelect for Role with EntitySelectWithAdd */}
            <EntitySelectWithAdd
              entityType="role"
              label="Rôle"
              options={roleOptions}
              value={form.role}
              onChange={value => setForm(f => ({ ...f, role: value }))}
              required
            />

            <ModernInput
              label="Date d'embauche"
              name="hire_date"
              type="date"
              value={form.hire_date}
              onChange={handleChange}
              icon={<Calendar className="w-5 h-5" />}
            />

            <ModernInput
              label="ID du magasin"
              name="store_id"
              value={form.store_id}
              onChange={handleChange}
              placeholder="Ex: 1"
              type="number"
            />
          </div>

          <div className="flex items-center justify-end space-x-3 pt-4">
            <ModernButton
              type="button"
              variant="secondary"
              onClick={() => setShowForm(false)}
            >
              Annuler
            </ModernButton>
            <ModernButton
              type="submit"
              variant="primary"
              icon={<Save className="w-4 h-4" />}
              disabled={submitLoading}
            >
              {submitLoading ? 'Enregistrement...' : (editingId ? 'Modifier' : 'Ajouter')}
            </ModernButton>
          </div>
        </form>
      </ModernModal>
    </div>
  );
};

export default Employees; 